import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import logoJRS from '../assets/logo_jrs.png';
import api, { getSaldosHora, getFuncionariosResumo, getDepartamentos, updateSaldoHora } from '../services/api';

const decToHHMM = v => {
  if (v == null) return '—';
  const neg = v < 0;
  const abs = Math.abs(v);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${neg ? '-' : ''}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const DIAS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const diaSemana = iso => iso ? DIAS_PT[new Date(iso + 'T00:00:00').getDay()] : '';

const isoToBR = iso => { if (!iso || !iso.includes('-')) return iso; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
const parseNumeroProposta = obs => { const m = (obs || '').match(/\[PROPOSTA:(\d{6}-\d{4})\]/); return m ? m[1] : null; };

export default function Compensacoes() {
  const [itens, setItens] = useState([]);
  const [saldosRaw, setSaldosRaw] = useState({}); // id -> registro SaldoHora completo
  const [funcs, setFuncs] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [filtros, setFiltros] = useState({ funcionario: '', status: '', departamento: '' });
  const [agruparPor, setAgruparPor] = useState('data'); // 'data' | 'funcionario' | ''
  const [loading, setLoading] = useState(false);

  // modal de ação
  const [modal, setModal] = useState(false);
  const [itemAtivo, setItemAtivo] = useState(null);
  const [acaoForm, setAcaoForm] = useState({ realizado: true, motivo: '', nova_data: '' });
  const [salvando, setSalvando] = useState(false);
  // modal de detalhe (motivo)
  const [detalheModal, setDetalheModal] = useState(false);
  const [detalheItem, setDetalheItem] = useState(null);

  useEffect(() => { getFuncionariosResumo().then(r => setFuncs(r.data)); }, []);
  useEffect(() => { getDepartamentos({ ativo: true }).then(r => setDepartamentos(r.data.results || r.data)).catch(() => {}); }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const primeira = await getSaldosHora({ tipo: 'COMPENSAR_DEPOIS', page_size: 200 });
      const data0 = primeira.data;
      let registros = data0.results || data0;
      let next = data0.next || null;
      while (next) {
        const nextPath = next.replace(/^https?:\/\/[^/]+/, '').replace('/api', '');
        const resp = await api.get(nextPath);
        registros = registros.concat(resp.data.results || resp.data);
        next = resp.data.next || null;
      }

      const rawMap = {};
      registros.forEach(s => { rawMap[s.id] = s; });
      setSaldosRaw(rawMap);

      const flat = [];
      for (const s of registros) {
        if (s.autorizado === false) continue; // proposta pendente de aprovação
        const funcId = s.registro_funcionario_id;
        if (filtros.funcionario && String(funcId) !== String(filtros.funcionario)) continue;
        (s.compensacoes || []).forEach((c, idx) => {
          if (!c.data) return;
          const isReagendado = !!c.reagendado_de;
          const status = c.realizado ? 'realizado' : c.nao_realizado ? 'pendente_reagendado' : isReagendado ? 'reagendado' : 'pendente';
          if (filtros.status && filtros.status !== status) return;
          flat.push({
            key: `${s.id}-${idx}`,
            saldo_id: s.id,
            comp_idx: idx,
            funcionario_nome: s.registro_funcionario_nome || '—',
            funcionario_id: funcId,
            debito_data: s.registro_data || '—',
            debito_saldo_min: s.registro_saldo_horas != null ? Math.abs(Math.round(s.registro_saldo_horas * 60)) : null,
            comp_data: c.data,
            comp_minutos: parseInt(c.minutos) || 0,
            realizado: !!c.realizado,
            realizado_em: c.realizado_em || null,
            nao_realizado: !!c.nao_realizado,
            reagendado_de: c.reagendado_de || null,
            is_reagendado: isReagendado,
            motivo: c.motivo || '',
            nova_data: c.nova_data || '',
            status,
          });
        });
      }
      flat.sort((a, b) => {
        if (agruparPor === 'funcionario') {
          const nCmp = a.funcionario_nome.localeCompare(b.funcionario_nome, 'pt-BR');
          return nCmp !== 0 ? nCmp : a.comp_data.localeCompare(b.comp_data);
        }
        return a.comp_data.localeCompare(b.comp_data);
      });
      setItens(flat);
    } finally { setLoading(false); }
  }, [filtros, agruparPor]);

  useEffect(() => { carregar(); }, [carregar]);

  const itensFiltrados = filtros.departamento
    ? itens.filter(item => {
        const func = funcs.find(f => String(f.id) === String(item.funcionario_id));
        return func && String(func.departamento) === String(filtros.departamento);
      })
    : itens;

  const porFunc = {};
  itensFiltrados.forEach(i => {
    porFunc[i.funcionario_nome] = (porFunc[i.funcionario_nome] || 0) + i.comp_minutos;
  });

  const porDia = {};
  itensFiltrados.forEach(i => {
    porDia[i.comp_data] = (porDia[i.comp_data] || 0) + i.comp_minutos;
  });

  // Itens bloqueados: realizados OU nao_realizado com reagendamento pendente
  const lockedKeys = new Set();
  itensFiltrados.forEach(item => {
    if (item.realizado) {
      lockedKeys.add(item.key);
    } else if (item.nao_realizado) {
      const temReagendamentoPendente = itensFiltrados.some(other =>
        other.saldo_id === item.saldo_id &&
        other.reagendado_de === item.comp_data &&
        !other.realizado
      );
      if (temReagendamentoPendente) lockedKeys.add(item.key);
    }
  });

  // eslint-disable-next-line no-unused-vars
  const gerarAcordoPDF = (item) => {
    const saldo = saldosRaw[item.saldo_id];
    if (!saldo) return;
    const func = funcs.find(f => String(f.id) === String(saldo.registro_funcionario_id));
    const cpfFunc = func?.cpf ? func.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '—';
    const comps = saldo.compensacoes || [];
    const totalCompMins = comps.reduce((s, c) => s + (parseInt(c.minutos) || 0), 0);
    const debitoHoras = saldo.registro_saldo_horas != null ? decToHHMM(Math.abs(saldo.registro_saldo_horas)) : '—';
    const numeroProposta = parseNumeroProposta(saldo.observacao);
    const agora = new Date();
    const dataHoje = `${String(agora.getDate()).padStart(2, '0')}/${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()}`;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const ML = 20, MR = 20, CW = pw - ML - MR;
    let y = 15;

    doc.addImage(logoJRS, 'PNG', pw - MR - 33, y, 33, 13);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(15, 23, 42);
    doc.text('ACORDO DE COMPENSAÇÃO', pw / 2, y + 4, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(71, 85, 105);
    if (numeroProposta) doc.text(`Proposta Nº ${numeroProposta}`, pw / 2, y + 10, { align: 'center' });
    doc.text('JRS SERVIÇOS EMPRESARIAIS LTDA · CNPJ nº 24.028.923/0001-10', pw / 2, numeroProposta ? y + 15 : y + 10, { align: 'center' });
    y += 26;
    doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3); doc.line(ML, y, pw - MR, y); y += 7;

    const justLine = (line, x, lineY, width) => {
      const words = line.trim().split(' ').filter(w => w.length > 0);
      if (words.length <= 1) { doc.text(line, x, lineY); return; }
      const ww = words.map(w => doc.getTextWidth(w));
      const gap = (width - ww.reduce((a, b) => a + b, 0)) / (words.length - 1);
      let cx = x;
      words.forEach((word, i) => { doc.text(word, cx, lineY); cx += ww[i] + gap; });
    };

    const bloco = (labelBold, texto) => {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
      const lw = doc.getTextWidth(labelBold);
      doc.text(labelBold, ML, y);
      doc.setFont('helvetica', 'normal');
      const firstW = CW - lw - 1.5;
      const p1 = doc.splitTextToSize(texto, firstW)[0];
      justLine(p1, ML + lw + 1.5, y, firstW);
      const resto = doc.splitTextToSize(texto.slice(p1.length).trimStart(), CW);
      resto.forEach((line, i) => { y += 5; if (i === resto.length - 1) doc.text(line, ML, y); else justLine(line, ML, y, CW); });
      y += 6;
    };

    const campo = (label, valor) => {
      doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(71, 85, 105);
      doc.text(label, ML, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 42);
      doc.text(' ' + valor, ML + doc.getTextWidth(label) + 2, y); y += 6.5;
    };

    bloco('EMPREGADOR: ', 'JRS SERVIÇOS EMPRESARIAIS LTDA, inscrita no CNPJ 24.028.923/0001-10 e CRC PE-001898/O-1, com sede na Av. Agamenon Magalhães, nº 4318, sala 1308, Derby, Recife-PE, CEP 52010-040, representada pelo sócio-administrador JOSÉ ROBERTO DOS SANTOS JÚNIOR, brasileiro, casado sob o regime de comunhão parcial de bens, nascido em 29/09/1986, contador, CPF/MF nº 055.450.124-43, portador da carteira de identidade nº 6.381.044 SSP/PE, residente e domiciliado na Rua Airton Senna da Silva, nº 100, Casa 19, Chã de Cruz, Paudalho/PE.');
    y += 2;
    bloco('EMPREGADO/ESTAGIÁRIO: ', `${saldo.registro_funcionario_nome}   inscrito(a) sob o CPF/MF nº ${cpfFunc}.`);
    y += 2;
    bloco('DELIBERAÇÃO: ', 'As partes acima acordam que a jornada não trabalhada devido assuntos pessoais, será compensada, de forma integral, sem prejuízo do salário, conforme detalhado na tabela abaixo. Fica ciente o empregado que o labor nos dias indicados não configura horas extras, uma vez que haverá a respectiva compensação correspondente a integralidade do período na forma legal, conforme indicado.');
    y += 4;

    doc.setDrawColor(226, 232, 240); doc.line(ML, y, pw - MR, y); y += 7;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
    doc.text('PERÍODO DA AUSÊNCIA', ML, y); y += 8;
    campo('Motivo Ausência:', 'Atraso');
    campo('Data da Ausência:', isoToBR(saldo.registro_data) || '—');
    campo('Quantidade de horas não trabalhadas:', debitoHoras);

    y += 3; doc.setDrawColor(226, 232, 240); doc.line(ML, y, pw - MR, y); y += 7;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
    doc.text('PROPOSTA DE COMPENSAÇÃO:', ML, y); y += 8;

    const col1 = 70, col2 = 50, rowH = 7;
    const tableW = col1 + col2;
    doc.setFillColor(241, 245, 249);
    doc.rect(ML, y, tableW, rowH, 'F');
    doc.setDrawColor(209, 213, 219); doc.setLineWidth(0.3);
    doc.rect(ML, y, tableW, rowH * (comps.length + 2), 'S');
    doc.line(ML + col1, y, ML + col1, y + rowH * (comps.length + 2));
    doc.line(ML, y + rowH, ML + tableW, y + rowH);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(71, 85, 105);
    doc.text('Data de Compensação', ML + 4, y + 5);
    doc.text('Horas a Compensar', ML + col1 + 4, y + 5);
    y += rowH;
    comps.forEach((c, i) => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
      doc.text(isoToBR(c.data) || '—', ML + 4, y + 5);
      doc.text(decToHHMM(parseInt(c.minutos) / 60), ML + col1 + 4, y + 5);
      if (i < comps.length - 1) doc.line(ML, y + rowH, ML + tableW, y + rowH);
      y += rowH;
    });
    doc.setDrawColor(209, 213, 219); doc.line(ML, y, ML + tableW, y);
    doc.setFillColor(241, 245, 249); doc.rect(ML, y, tableW, rowH, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
    doc.text('TOTAL', ML + 4, y + 5);
    doc.text(decToHHMM(totalCompMins / 60), ML + col1 + 4, y + 5);
    y += rowH + 12;

    doc.setTextColor(71, 85, 105); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    doc.text(`Recife - PE, ${dataHoje}`, ML, y); y += 16;

    doc.setDrawColor(150, 150, 150); doc.setLineWidth(0.3);
    doc.line(ML, y, ML + 72, y); y += 5;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(15, 23, 42);
    doc.text(saldo.registro_funcionario_nome, ML, y); y += 4.5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text(`CPF: ${cpfFunc}`, ML, y); y += 4.5;
    doc.text('Colaborador(a)', ML, y); y += 16;

    doc.line(ML, y, ML + 72, y); y += 5;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(15, 23, 42);
    doc.text('MARCOS AURÉLIO BRITO DOS SANTOS', ML, y); y += 4.5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text('CPF: 023.871.644-90', ML, y); y += 4.5;
    doc.text('Diretor Técnico', ML, y);

    doc.save(`acordo_${numeroProposta || saldo.registro_data}_${saldo.registro_funcionario_nome.split(' ')[0]}.pdf`);
  };

  const abrirAcao = (item) => {
    setItemAtivo(item);
    setAcaoForm({
      realizado: item.realizado ? true : !item.nao_realizado,
      motivo: item.motivo || '',
      nova_data: item.nova_data || '',
    });
    setModal(true);
  };

  const salvarAcao = async () => {
    setSalvando(true);
    try {
      const saldo = saldosRaw[itemAtivo.saldo_id];
      const comps = [...(saldo.compensacoes || [])];
      const c = { ...comps[itemAtivo.comp_idx] };
      if (acaoForm.realizado) {
        // baixa na data do próprio agendamento (não hoje)
        c.realizado = true;
        c.realizado_em = itemAtivo.comp_data;
        delete c.nao_realizado;
        delete c.motivo;
        delete c.nova_data;
      } else {
        c.nao_realizado = true;
        c.motivo = acaoForm.motivo;
        c.nova_data = acaoForm.nova_data;
        delete c.realizado;
        delete c.realizado_em;
        // adiciona novo item para a data reagendada (se ainda não existir)
        const jaExiste = comps.some((x, i) =>
          i !== itemAtivo.comp_idx && x.data === acaoForm.nova_data && x.reagendado_de === c.data
        );
        if (!jaExiste) {
          comps.push({
            data: acaoForm.nova_data,
            minutos: c.minutos,
            reagendado_de: c.data,
          });
        }
      }
      comps[itemAtivo.comp_idx] = c;
      await updateSaldoHora(itemAtivo.saldo_id, { ...saldo, compensacoes: comps });
      setModal(false);
      carregar();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao salvar'));
    } finally { setSalvando(false); }
  };

  const cancelarRealizacao = async () => {
    setSalvando(true);
    try {
      const saldo = saldosRaw[itemAtivo.saldo_id];
      const comps = [...(saldo.compensacoes || [])];
      const c = { ...comps[itemAtivo.comp_idx] };
      delete c.realizado;
      delete c.realizado_em;
      comps[itemAtivo.comp_idx] = c;
      await updateSaldoHora(itemAtivo.saldo_id, { ...saldo, compensacoes: comps });
      setModal(false);
      carregar();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao cancelar'));
    } finally { setSalvando(false); }
  };

  const badgeStatus = item => {
    if (item.realizado)
      return <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '2px 9px', fontSize: 'var(--fs-xs)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><i className="bi bi-check-circle-fill" />Realizado</span>;
    if (item.nao_realizado)
      return <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 4, padding: '2px 9px', fontSize: 'var(--fs-xs)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><i className="bi bi-x-circle-fill" />Não realizado</span>;
    if (item.is_reagendado)
      return <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 4, padding: '2px 9px', fontSize: 'var(--fs-xs)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><i className="bi bi-arrow-repeat" />Reagendado</span>;
    return <span style={{ background: '#f1f5f9', color: 'var(--text-muted)', borderRadius: 4, padding: '2px 9px', fontSize: 'var(--fs-xs)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><i className="bi bi-clock" />Pendente</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
          <i className="bi bi-calendar2-check-fill" />
        </div>
        <div>
          <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Controle de Ponto</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Compensações</div>
        </div>
      </div>


      {/* Filtros */}
      <div className="filter-bar">
        <div style={{ flex: 2, minWidth: 160 }}>
          <label className="form-label">Funcionário</label>
          <select className="form-select" value={filtros.funcionario} onChange={e => setFiltros(f => ({ ...f, funcionario: e.target.value }))}>
            <option value="">Todos</option>
            {funcs.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 150 }}>
          <label className="form-label">Departamento</label>
          <select className="form-select" value={filtros.departamento} onChange={e => setFiltros(f => ({ ...f, departamento: e.target.value }))}>
            <option value="">Todos</option>
            {departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 140 }}>
          <label className="form-label">Status</label>
          <select className="form-select" value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos</option>
            <option value="pendente">Pendente</option>
            <option value="reagendado">Reagendado</option>
            <option value="pendente_reagendado">Não realizado</option>
            <option value="realizado">Realizado</option>
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end', display: 'flex', gap: 6 }}>
          {[
            { key: 'data',        icon: 'bi-calendar3',    label: 'Por Data' },
            { key: 'funcionario', icon: 'bi-person-lines-fill', label: 'Por Funcionário' },
          ].map(op => (
            <button key={op.key} type="button"
              onClick={() => setAgruparPor(v => v === op.key ? '' : op.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 13px', borderRadius: 6, fontWeight: 600, fontSize: 'var(--fs-xs)', cursor: 'pointer',
                border: `2px solid ${agruparPor === op.key ? '#1d4ed8' : '#e2e8f0'}`,
                background: agruparPor === op.key ? '#eff6ff' : '#f8fafc',
                color: agruparPor === op.key ? '#1d4ed8' : 'var(--text-muted)',
              }}>
              <i className={`bi ${op.icon}`} />
              {op.label}
            </button>
          ))}
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button className="btn-secondary-custom" onClick={carregar}>
            <i className="bi bi-search" />Buscar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="card-ponto">
        <div className="table-responsive">
          <table className="table-ponto" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 32 }}>Data de compensação</th>
                <th>Funcionário</th>
                <th style={{ textAlign: 'center' }}>Tempo</th>
                <th>Data do débito</th>
                <th style={{ textAlign: 'center' }}>Débito original</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ width: 36 }}></th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></td></tr>}
              {!loading && itensFiltrados.map((item, idx) => {
                const prevItem = idx > 0 ? itensFiltrados[idx - 1] : null;
                const [y, m, d] = item.comp_data.split('-');
                const dataBR = `${d}/${m}/${y}`;
                const minsDia = porDia[item.comp_data];

                const novaDataSep = agruparPor === 'data' && item.comp_data !== (prevItem?.comp_data ?? null);
                const novaFuncSep = agruparPor === 'funcionario' && item.funcionario_nome !== (prevItem?.funcionario_nome ?? null);

                return (
                  <React.Fragment key={item.key}>
                    {novaDataSep && (
                      <tr>
                        <td colSpan={8} style={{ padding: '6px 12px', background: '#f1f5f9', borderTop: idx > 0 ? '2px solid #cbd5e1' : 'none', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#1e293b' }}>{dataBR}</span>
                              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', background: '#e2e8f0', borderRadius: 4, padding: '1px 7px', fontWeight: 600 }}>{diaSemana(item.comp_data)}</span>
                            </div>
                            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: '#1d4ed8' }}>Total do dia: {decToHHMM(minsDia / 60)}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {novaFuncSep && (
                      <tr>
                        <td colSpan={8} style={{ padding: '6px 12px', background: '#f1f5f9', borderTop: idx > 0 ? '2px solid #cbd5e1' : 'none', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <i className="bi bi-person-fill" style={{ color: '#1d4ed8', fontSize: 'var(--fs-base)' }} />
                              <span style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#1e293b' }}>{item.funcionario_nome}</span>
                            </div>
                            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: '#1d4ed8' }}>Total: {decToHHMM(porFunc[item.funcionario_nome] / 60)}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr style={{ opacity: item.realizado ? 0.6 : 1 }}>
                      <td style={{ fontWeight: agruparPor === 'data' ? 400 : 600, color: agruparPor === 'data' ? 'var(--text-subtle)' : '#1e293b', fontSize: 'var(--fs-sm)', whiteSpace: 'nowrap', paddingLeft: 32 }}>
                        {dataBR}
                        {agruparPor !== 'data' && <span style={{ marginLeft: 6, fontSize: 'var(--fs-xs)', color: 'var(--text-subtle)', fontWeight: 400 }}>{diaSemana(item.comp_data)}</span>}
                      </td>
                      <td style={{ fontWeight: agruparPor === 'funcionario' ? 400 : 600, color: agruparPor === 'funcionario' ? '#94a3b8' : '#1e293b' }}>{item.funcionario_nome}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ background: item.realizado ? '#f0fdf4' : '#eff6ff', color: item.realizado ? '#15803d' : '#1d4ed8', borderRadius: 3, padding: '2px 10px', fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                          {decToHHMM(item.comp_minutos / 60)}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>{item.debito_data}</td>
                      <td style={{ textAlign: 'center' }}>
                        {item.debito_saldo_min != null
                          ? <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 3, padding: '2px 8px', fontSize: 'var(--fs-xs)', fontWeight: 700 }}>-{decToHHMM(item.debito_saldo_min / 60)}</span>
                          : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{badgeStatus(item)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(item.nao_realizado || item.is_reagendado) && (
                          <button
                            className="btn-icon"
                            onClick={() => { setDetalheItem(item); setDetalheModal(true); }}
                            title="Ver detalhes"
                            style={{ color: item.nao_realizado ? '#dc2626' : '#d97706' }}
                          >
                            <i className="bi bi-info-circle-fill" />
                          </button>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <button
                            onClick={() => abrirAcao(item)}
                            title={lockedKeys.has(item.key) ? 'Consultar (edição bloqueada)' : 'Registrar situação'}
                            className="btn-icon"
                            style={{ color: lockedKeys.has(item.key) ? 'var(--text-subtle)' : 'var(--text-muted)' }}
                          >
                            <i className={`bi ${lockedKeys.has(item.key) ? 'bi-lock-fill' : 'bi-pencil-square'}`} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {!loading && itensFiltrados.length === 0 && (
                <tr><td colSpan={8}><div className="empty-state"><i className="bi bi-calendar-check" /><p>Nenhuma compensação encontrada</p></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de detalhe (motivo / reagendamento) */}
      {detalheModal && detalheItem && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setDetalheModal(false)}>
          <div className="modal-box" style={{ maxWidth: 400 }}>
            <div className="modal-header-custom" style={{ background: detalheItem.nao_realizado ? 'linear-gradient(135deg,#7f1d1d,#dc2626)' : 'linear-gradient(135deg,#78350f,#f59e0b)' }}>
              <div>
                <h5 style={{ color: '#fff', margin: 0 }}>
                  <i className={`bi ${detalheItem.nao_realizado ? 'bi-x-circle-fill' : 'bi-arrow-repeat'} me-2`} />
                  {detalheItem.nao_realizado ? 'Não realizado' : 'Reagendado'}
                </h5>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', marginTop: 3 }}>
                  {detalheItem.funcionario_nome} — {isoToBR(detalheItem.comp_data)}
                </div>
              </div>
              <button className="modal-close" style={{ color: '#fff' }} onClick={() => setDetalheModal(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <div className="modal-body-custom" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {detalheItem.nao_realizado && (
                <>
                  <div>
                    <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Motivo</div>
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 'var(--fs-base)', color: '#7f1d1d' }}>
                      {detalheItem.motivo || '—'}
                    </div>
                  </div>
                  {detalheItem.nova_data && (
                    <div>
                      <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Nova data programada</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                        <i className="bi bi-calendar2-event" style={{ color: '#d97706' }} />
                        <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>{isoToBR(detalheItem.nova_data)}</span>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-subtle)' }}>{['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][new Date(detalheItem.nova_data+'T00:00:00').getDay()]}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
              {detalheItem.is_reagendado && detalheItem.reagendado_de && (
                <div>
                  <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Reagendado de</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                    <i className="bi bi-arrow-right-circle" style={{ color: '#d97706' }} />
                    <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>{isoToBR(detalheItem.reagendado_de)}</span>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-subtle)' }}>→ {isoToBR(detalheItem.comp_data)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer-custom">
              <button type="button" className="btn-secondary-custom" onClick={() => setDetalheModal(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de ação */}
      {modal && itemAtivo && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 460 }}>
            <div className="modal-header-custom" style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a8a)' }}>
              <div>
                <h5 style={{ color: '#fff', margin: 0 }}><i className="bi bi-calendar2-check me-2" />Situação da Compensação</h5>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
                  {itemAtivo.funcionario_nome} — {isoToBR(itemAtivo.comp_data)} &nbsp;|&nbsp;
                  <strong style={{ color: '#fff' }}>{decToHHMM(itemAtivo.comp_minutos / 60)}</strong>
                </div>
              </div>
              <button className="modal-close" style={{ color: '#fff' }} onClick={() => setModal(false)}><i className="bi bi-x-lg" /></button>
            </div>

            <div className="modal-body-custom">
              {/* Aviso de bloqueio */}
              {lockedKeys.has(itemAtivo.key) && (
                itemAtivo.realizado ? (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: '1rem', flexShrink: 0 }} />
                    <div style={{ fontSize: 'var(--fs-sm)', color: '#14532d' }}>
                      <strong>Compensação realizada.</strong> Para editar, cancele o registro de realização abaixo.
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <i className="bi bi-lock-fill" style={{ color: '#d97706', fontSize: '1rem', flexShrink: 0 }} />
                    <div style={{ fontSize: 'var(--fs-sm)', color: '#92400e' }}>
                      <strong>Edição bloqueada.</strong> Existe um reagendamento pendente para este registro. Conclua ou cancele o reagendamento antes de editar.
                    </div>
                  </div>
                )
              )}
              {/* Seleção realizado / não realizado */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button type="button" onClick={() => setAcaoForm(f => ({ ...f, realizado: true }))}
                  style={{ flex: 1, padding: '12px 8px', borderRadius: 8, border: '2px solid', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--fs-base)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    borderColor: acaoForm.realizado ? '#16a34a' : '#e2e8f0',
                    background: acaoForm.realizado ? '#f0fdf4' : '#f8fafc',
                    color: acaoForm.realizado ? '#14532d' : '#64748b' }}>
                  <i className="bi bi-check-circle-fill" style={{ fontSize: '1.4rem' }} />
                  {itemAtivo.is_reagendado ? 'Reagendamento realizado' : 'Compensação realizada'}
                </button>
                <button type="button" onClick={() => setAcaoForm(f => ({ ...f, realizado: false }))}
                  style={{ flex: 1, padding: '12px 8px', borderRadius: 8, border: '2px solid', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--fs-base)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    borderColor: !acaoForm.realizado ? '#f59e0b' : '#e2e8f0',
                    background: !acaoForm.realizado ? '#fffbeb' : '#f8fafc',
                    color: !acaoForm.realizado ? '#92400e' : '#64748b' }}>
                  <i className="bi bi-x-circle-fill" style={{ fontSize: '1.4rem' }} />
                  Não foi realizada
                </button>
              </div>

              {/* Campos para não realizado */}
              {!acaoForm.realizado && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      Motivo <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <textarea className="form-control" rows={2}
                      placeholder="Descreva o motivo de não ter sido realizada"
                      value={acaoForm.motivo}
                      onChange={e => setAcaoForm(f => ({ ...f, motivo: e.target.value }))}
                      style={{ borderColor: !acaoForm.motivo.trim() ? '#fca5a5' : undefined }}
                    />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      Nova data programada <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input type="date" className="form-control date-picker-visible"
                      value={acaoForm.nova_data}
                      onChange={e => setAcaoForm(f => ({ ...f, nova_data: e.target.value }))}
                      style={{ borderColor: !acaoForm.nova_data ? '#fca5a5' : undefined }}
                    />
                  </div>
                </div>
              )}

              {acaoForm.realizado && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: '1.2rem' }} />
                  <div style={{ fontSize: 'var(--fs-sm)', color: '#14532d' }}>
                    {itemAtivo.is_reagendado
                      ? <>Será registrada como <strong>concluída</strong> na data do reagendamento ({isoToBR(itemAtivo.comp_data)}).</>
                      : <>Será registrada como <strong>concluída</strong> na data do agendamento ({isoToBR(itemAtivo.comp_data)}).</>
                    }
                  </div>
                </div>
              )}

              {/* Plano de compensação completo */}
              {(() => {
                const saldo = saldosRaw[itemAtivo.saldo_id];
                const comps = saldo?.compensacoes || [];
                if (!comps.length) return null;
                const devidas = itemAtivo.debito_saldo_min ?? 0;
                const compensadas = comps.filter(c => c.realizado).reduce((s, c) => s + (parseInt(c.minutos) || 0), 0);
                const saldoMin = devidas - compensadas;
                const compStatus = c => c.realizado
                  ? { label: 'Realizado',     color: '#15803d', dot: '#16a34a' }
                  : c.nao_realizado
                  ? { label: 'Não realizado', color: '#991b1b', dot: '#dc2626' }
                  : c.reagendado_de
                  ? { label: 'Reagendado',    color: '#92400e', dot: '#f59e0b' }
                  : { label: 'Pendente',      color: '#64748b', dot: '#94a3b8' };
                return (
                  <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    {/* Cabeçalho */}
                    <div style={{ background: '#f8fafc', padding: '8px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Plano de Compensação</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-xs)', color: 'var(--text-subtle)' }}>
                        <i className="bi bi-calendar-minus" style={{ color: '#dc2626' }} />
                        <span>Débito <strong style={{ color: '#dc2626' }}>{isoToBR(itemAtivo.debito_data)}</strong></span>
                        {itemAtivo.debito_saldo_min != null && <span style={{ fontWeight: 700, color: '#dc2626' }}>−{decToHHMM(itemAtivo.debito_saldo_min / 60)}</span>}
                      </div>
                    </div>

                    {/* Totalizadores */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr 1px 1fr', background: '#fff' }}>
                      {[
                        { label: 'Devidas',      value: decToHHMM(devidas / 60),     color: '#dc2626' },
                        null,
                        { label: 'Compensadas',  value: decToHHMM(compensadas / 60), color: '#16a34a' },
                        null,
                        { label: 'Saldo',        value: (saldoMin > 0 ? '−' : '') + decToHHMM(Math.abs(saldoMin) / 60), color: saldoMin > 0 ? '#dc2626' : '#16a34a' },
                      ].map((col, i) => col === null
                        ? <div key={i} style={{ background: '#e2e8f0' }} />
                        : (
                          <div key={i} style={{ padding: '10px 8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{col.label}</div>
                            <div style={{ fontSize: '1rem', fontWeight: 800, color: col.color, letterSpacing: '-0.02em' }}>{col.value}</div>
                          </div>
                        )
                      )}
                    </div>

                    {/* Lista de entradas */}
                    <div style={{ borderTop: '1px solid #e2e8f0' }}>
                      {comps.map((c, idx) => {
                        const st = compStatus(c);
                        const isAtivo = c.data === itemAtivo.comp_data;
                        return (
                          <div key={idx} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '7px 14px',
                            background: isAtivo ? '#f0f7ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                            borderLeft: `3px solid ${isAtivo ? '#3b82f6' : 'transparent'}`,
                            borderBottom: idx < comps.length - 1 ? '1px solid #f1f5f9' : 'none',
                            opacity: c.nao_realizado ? 0.55 : 1,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: isAtivo ? 700 : 500, color: c.nao_realizado ? 'var(--text-subtle)' : '#1e293b', textDecoration: c.nao_realizado ? 'line-through' : 'none', minWidth: 72 }}>
                                {isoToBR(c.data)}
                              </span>
                              <span style={{ fontSize: 'var(--fs-2xs)', color: '#cbd5e1', fontWeight: 500 }}>{diaSemana(c.data)}</span>
                              <span style={{ background: isAtivo ? '#dbeafe' : '#f1f5f9', color: isAtivo ? '#1d4ed8' : '#475569', borderRadius: 4, padding: '1px 8px', fontSize: 'var(--fs-xs)', fontWeight: 700 }}>
                                {decToHHMM(parseInt(c.minutos) / 60)}
                              </span>
                              {c.reagendado_de && (
                                <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <i className="bi bi-arrow-return-right" />{isoToBR(c.reagendado_de)}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, display: 'inline-block', flexShrink: 0 }} />
                                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: st.color }}>{st.label}</span>
                              </div>
                              {c.nao_realizado && c.nova_data && (
                                <span style={{ fontSize: 'var(--fs-2xs)', color: '#f59e0b', fontWeight: 600 }}>
                                  <i className="bi bi-arrow-right me-1" />{isoToBR(c.nova_data)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="modal-footer-custom" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-secondary-custom" onClick={() => setModal(false)}>Fechar</button>
                {itemAtivo.realizado && (
                  <button type="button" className="btn-danger-custom" disabled={salvando} onClick={cancelarRealizacao}>
                    {salvando ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-arrow-counterclockwise" />Cancelar realização</>}
                  </button>
                )}
              </div>
              <button type="button" className="btn-primary-custom"
                style={{ background: acaoForm.realizado ? 'linear-gradient(135deg,#14532d,#16a34a)' : 'linear-gradient(135deg,#78350f,#f59e0b)' }}
                disabled={salvando || lockedKeys.has(itemAtivo.key) || (!acaoForm.realizado && (!acaoForm.motivo.trim() || !acaoForm.nova_data))}
                onClick={salvarAcao}>
                {salvando ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" />Salvar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
